/* eslint-disable react/style-prop-object */
import { useState } from "react";
import { Attention, Typography } from "@optimizely/axiom";
import { datadogRum } from "@datadog/browser-rum";
import { IdentityProvider } from "../../../../domain/IdentityProvider";
import { emitToast } from "../../../../lib/toaster-utils";
import { ConfirmationDialog } from "../../ConfirmationDialog/ConfirmationDialog";
import { UNEXPECTED_ERROR } from "../../../../constants/constants";

export const SCIMDialog = ({
    identityProvider,
    onClose,
    onSave
}: {
    identityProvider: IdentityProvider;
    onClose: () => void;
    onSave: ({ scimKey }: { scimKey: string }) => Promise<IdentityProvider>;
}) => {
    const { scimApiKey } = identityProvider;
    const [scimKeyValue] = useState<string>(scimApiKey || "");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSave = async () => {
        setIsSaving(true);
        setError("");
        try {
            await onSave({ scimKey: scimKeyValue });
            onClose();
            emitToast({
                message: "SCIM key created successfully."
            });
        } catch (e) {
            if (Array.isArray(e)) {
                const [error] = e as any[];
                datadogRum.addError(error);
                console.error(error);
                setError(error?.message || UNEXPECTED_ERROR);
            } else {
                datadogRum.addError(e);
                console.error(e);
                setError(UNEXPECTED_ERROR);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ConfirmationDialog
            onCancel={onClose}
            onConfirmation={handleSave}
            callToActionText="Save"
            error={error}
            loading={isSaving}
            title="Enable SCIM"
            style="highlight"
        >
            {!!error && (
                <Attention type="bad-news" className="push--bottom">
                    {error}
                </Attention>
            )}
            <Typography tag="p" type="caption" className="push-quad--bottom">
                You are about to enable SCIM for the following SSO Connection:
            </Typography>
            <Typography tag="p" type="body" className="push-quad--bottom">
                {identityProvider.idpName}
            </Typography>
            <Attention type="bad-news" className="push-quad--bottom">
                <strong>Important:</strong> Enabling SCIM will disable Just-In-Time (JIT) provisioning, group sync, and
                remove any configured domains. It is also recommended to remove the Group claim from your SSO
                application before configuring SCIM.
            </Attention>
        </ConfirmationDialog>
    );
};

SCIMDialog.displayName = "SCIMDialog";
